import { useCallback, useMemo } from 'react';
import { useAccount, useMsal } from '@azure/msal-react';
import { InteractionRequiredAuthError, type AccountInfo } from '@azure/msal-browser';
import { loginRequest } from '../auth/msalConfig';

// Single source of truth for talking to /api/manage/*. (Originally named
// "admin" but Azure Static Web Apps reserves /api/admin/* and won't forward
// it to a linked backend — surfaces as a silent 404 from the SWA front-door
// regardless of what the backend has registered.) Acquires an access
// token silently via MSAL, attaches Authorization: Bearer, surfaces error
// codes from the API's fail() shape ({error, adminOk, details}) as a typed
// AdminApiError. Triggers an interactive sign-in if the silent acquire
// raises InteractionRequiredAuthError (consent revoked, tokens expired,
// password reset, etc.).

export class AdminApiError extends Error {
  code: string;
  status: number;
  details: unknown;
  adminOk: boolean;
  constructor(code: string, status: number, details: unknown, adminOk: boolean) {
    super(humanize(code, status));
    this.code = code;
    this.status = status;
    this.details = details;
    this.adminOk = adminOk;
  }
}

function humanize(code: string, status: number): string {
  switch (code) {
    case 'missing_bearer':
    case 'malformed_bearer':
    case 'invalid_token':         return 'Please sign in again.';
    case 'missing_role':          return 'You don\'t have permission to do that.';
    case 'validation_failed':     return 'Some fields need fixing.';
    case 'unknown_room':          return 'That room doesn\'t exist.';
    case 'unknown_binding':       return 'That paired kiosk doesn\'t exist.';
    case 'confirmation_mismatch': return 'Confirmation did not match the room ID.';
    case 'no_in_progress_meeting': return 'There\'s no meeting currently in progress for that room.';
    case 'room_misconfigured':    return 'The room exists but is misconfigured.';
    case 'room_inactive':         return 'That room is inactive. Activate it first.';
    case 'already_revoked':       return 'That kiosk is already revoked.';
    case 'must_revoke_first':     return 'Revoke the kiosk before deleting it.';
    case 'binding_write_failed':  return 'Couldn\'t save the kiosk change. Try again.';
    case 'graph_calendar_failed': return 'Microsoft Graph couldn\'t reach the calendar.';
    case 'release_unverified':    return 'Force-release wrote but couldn\'t confirm — check Outlook.';
    default:                      return code || `HTTP ${status}`;
  }
}

export function useAdminApi() {
  const { instance, accounts } = useMsal();
  const account = useAccount(accounts[0] ?? null);

  const getToken = useCallback(async (acct: AccountInfo): Promise<string> => {
    try {
      const r = await instance.acquireTokenSilent({
        scopes: [loginRequest.scopes[0]],   // api://{spaClientId}/Rooms.Read
        account: acct,
      });
      return r.accessToken;
    } catch (e) {
      if (e instanceof InteractionRequiredAuthError) {
        // Re-route through the redirect flow; the page reload after will
        // pick up the fresh token. acquireTokenRedirect is fire-and-forget.
        await instance.acquireTokenRedirect({ scopes: [loginRequest.scopes[0]], account: acct });
      }
      throw e;
    }
  }, [instance]);

  const apiFetch = useCallback(async <T,>(path: string, init: RequestInit = {}): Promise<T> => {
    if (!account) throw new AdminApiError('not_signed_in', 401, null, true);
    const token = await getToken(account);

    const headers = new Headers(init.headers);
    headers.set('Authorization', `Bearer ${token}`);
    if (init.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');

    const res = await fetch(path, { ...init, headers });
    if (!res.ok) {
      let body: { error?: string; adminOk?: boolean; details?: unknown } = {};
      try { body = await res.json(); } catch { /* ignore */ }
      throw new AdminApiError(body.error ?? `http_${res.status}`, res.status, body.details, body.adminOk ?? false);
    }
    if (res.status === 204) return undefined as unknown as T;
    return (await res.json()) as T;
  }, [account, getToken]);

  return useMemo(() => ({
    apiFetch,
    signedIn: !!account,
    account,
  }), [apiFetch, account]);
}
